import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, undefined = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [setupRequired, setSetupRequired] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const s = await api.get("/setup/status");
      setSetupRequired(Boolean(s.data?.setup_required));
    } catch {
      setSetupRequired(false);
    }
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {
      setUser(undefined);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const setup = useCallback(async (email, password, name) => {
    setError(null);
    try {
      const r = await api.post("/setup", { email, password, name });
      setSetupRequired(false);
      setUser(r.data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(undefined);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, setupRequired, error, setError, login, setup, logout, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
